import { Flex, message, notification } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { fetchCreateOrder, fetchPayment, Order } from '~/services/pay';
import QRCode from 'react-qr-code';
import { CheckCircle, QrCode } from '@phosphor-icons/react';
import { retryCount } from '../../const';
import { trackEvent } from '~/utils/gtm';
import WeixinPayment_src from '~/assets/images/weixin_payment.png';

import { formatAmount } from '~/utils/function';
import { useAuth } from '~/context/AuthContext ';

import { getUserInfo } from '~/services/user';
import { getLoginModalStore } from '~/components/LoginModal/store';

import './index.less'; // 引入样式文件

// 前后端超时时间偏差
const offsetTime = 2 * 60;

const PayCode = ({ open, currentSelect }: any) => {
  const { userInfo, updateAuthInfo } = useAuth();
  const [api, contextHolder] = notification.useNotification();
  const [timerText, setTimerText] = useState<string>();
  const [timeoutShow, setTimeoutShow] = useState(false);
  const [orderData, setOrderData] = useState<Order>();
  const [orderLoading, setOrderLoading] = useState(false);

  const timerIntervalRef = useRef<number | NodeJS.Timeout | null>(null);
  const checkTimerRef = useRef<number | NodeJS.Timeout | null>(null);
  const shouldStopRef = useRef(false);

  const deleteTimerInterval = () => {
    if (timerIntervalRef.current !== null) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  };
  const deleteCheckTimer = () => {
    if (checkTimerRef.current !== null) {
      clearTimeout(checkTimerRef.current);
      checkTimerRef.current = null;
    }
  };
  const openNotification = (pauseOnHover: boolean) => () => {
    const isMembership = ['VIP_YEAR', 'SVIP_MONTH', 'SVIP_YEAR'].includes(currentSelect?.key);
    api.open({
      message: isMembership ? '购买成功' : '充值成功',
      description: isMembership
        ? '会员权益正在生效，请注意查看。感谢您的支持！'
        : '系统正在更新您的充值余额，请注意查看。感谢您的支持！',
      showProgress: true,
      icon: <CheckCircle size={32} color="#2ecc71" weight="fill" />,
      pauseOnHover,
    });
  };
  const onFinish = async () => {
    openNotification(true)();
    const { code: userCode, data } = await getUserInfo(userInfo?.id || '');
    if (userCode === 0 && data) {
      // 更新旧的local
      updateAuthInfo(data);
    }
  };

  // 倒计时功能,  5分钟，以秒计
  function startCountdown(timeLeft: number) {
    deleteTimerInterval();
    setTimeoutShow(false);

    function updateTimer() {
      if (timeLeft <= 0) {
        deleteCheckTimer();
        deleteTimerInterval();
        setTimerText('00:00');
        setTimeoutShow(true);
        return;
      }

      const minutes = Math.floor(timeLeft / 60);
      const seconds = timeLeft % 60;

      setTimerText(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      timeLeft--;
    }

    updateTimer();
    timerIntervalRef.current = setInterval(updateTimer, 1000);
  }

  const checkPayment = async () => {
    // 清除之前的定时器
    deleteCheckTimer();
    // 如果没有订单数据或者二维码未加载，则不再检查支付状态
    if (!userInfo.id || !open || orderLoading || timeoutShow || !orderData?.orderNO) {
      return;
    }

    try {
      const { code, data } = await fetchPayment(orderData.orderNO);

      // 检查完成后，再次确认是否应该停止
      if (!open || shouldStopRef.current) {
        return;
      }

      if (code === 0 && data.status === 'PAYED') {
        onFinish(); // 关闭支付弹窗
      } else {
        checkTimerRef.current = setTimeout(checkPayment, 2 * 1000); // 每2秒检查一次支付状态
      }
    } catch (error) {
      // 网络/服务端异常时不能就此停止轮询：用户可能已经付款，只是这一轮入账
      // 或查询失败了。弹窗仍打开且未超时就继续下一次检查。
      if (open && !shouldStopRef.current && !timeoutShow) {
        checkTimerRef.current = setTimeout(checkPayment, 2 * 1000);
      }
    }
  };

  const updateOrderData = (data: Order) => {
    setOrderData(data);
    startCountdown(Math.max(data.expire - offsetTime, 60));
  };

  const createOrder = async () => {
    if (!userInfo?.id) {
      getLoginModalStore().open = true;
      return;
    }
    // 推送事件到 GTM
    trackEvent('createOrder', {
      userId: userInfo?.id || '*',
      amount: currentSelect.value,
    });

    deleteCheckTimer();
    setTimeoutShow(false);
    setOrderLoading(true);

    let count = 0;
    const fetchCreateOrderWithRetry = async () => {
      if (!open) {
        return;
      }
      const {
        code,
        data,
        message: msg,
      } = await fetchCreateOrder({
        productID: currentSelect.key,
        productValue: currentSelect.value,
        ...currentSelect?.extra,
      });
      count++;
      if (code !== 0 || !data) {
        if (count < retryCount) {
          console.error(`创建订单失败，重试中... ${msg}`);
          fetchCreateOrderWithRetry();
        } else {
          setOrderLoading(false);
          message.error(msg || '创建订单失败，请稍后再试');
        }
        return;
      }
      shouldStopRef.current = false;
      updateOrderData(data);
      setOrderLoading(false);
    };
    fetchCreateOrderWithRetry();
  };

  const clearAll = () => {
    shouldStopRef.current = true;
    deleteTimerInterval();
    deleteCheckTimer();
    setOrderData(undefined);
  };

  useEffect(() => {
    if (orderData?.orderNO) {
      checkPayment();
    }
  }, [orderData?.orderNO]);

  useEffect(() => {
    if (!open) {
      clearAll();
    }
  }, [open]);

  useEffect(() => {
    if (open || (currentSelect || timeoutShow)) {
      createOrder();
    }
  }, [open, currentSelect, timeoutShow]);

  useEffect(() => {
    return () => {;
      clearAll();
    };
  }, []);

  return (
    <>
      {/* 这是一个提示组件 */}
      {contextHolder}
      <div className="payment-content mod-card">
        <Flex className="pay-type-container" justify="center">
          <Flex className="pay-wechat" align="center" justify="center" gap={12}>
            <img src={WeixinPayment_src} alt="微信支付" />
            微信支付
          </Flex>
        </Flex>

        <div className="qrcode-container">
          <div className="qrcode">
            {orderLoading || !orderData?.qrCodeURL ? (
              <Flex vertical align="center" justify="center" gap={4}>
                <QrCode weight="bold" size={96} />
                <span className="text-gray-300">加载中...</span>
              </Flex>
            ) : (
              <div className="qrcode-wrapper">
                <QRCode
                  value={orderData.qrCodeURL}
                  size={200} // 二维码尺寸
                  level="H" // 容错级别 (L, M, Q, H)
                  fgColor="#000000" // 二维码颜色
                  bgColor="#ffffff" // 背景颜色
                />
              </div>
            )}
          </div>
        </div>
        <div className="amount-section">
          <div className="amount">
            {formatAmount(
              orderLoading ? currentSelect?.value : orderData?.price || currentSelect?.value,
              { currency: '¥' }
            )}
          </div>
        </div>
        <div className="pay-warning-text">虚拟商品出售后不能退款，请按需购买！</div>

        {/* <div className="timer-section">
          <div className="timer-label">支付剩余时间</div>
          <div className="timer">{!orderLoading && orderData?.qrCodeURL ? timerText : '00:00'}</div>
        </div> */}
      </div>
    </>
  );
};
export default PayCode;
